(() => {
  const SELECTOR = '.cd-contact-stage';

  const splitHeading = (section) => {
    const heading = section.querySelector('[data-cd-contact-heading]');
    if (!heading || heading.dataset.cdContactSplit === 'true') return;

    const original = heading.textContent || '';
    heading.dataset.cdContactSplit = 'true';
    heading.setAttribute('aria-label', original.trim());

    const inner = document.createElement('span');
    inner.className = 'cd-contact-stage__heading-inner';
    inner.setAttribute('aria-hidden', 'true');

    let index = 0;

    original.split(/(\s+)/).forEach((token) => {
      if (!token) return;

      if (/^\s+$/.test(token)) {
        const space = document.createElement('span');
        space.className = 'cd-contact-stage__space';
        space.style.setProperty('--cd-contact-char-index', index);
        space.textContent = '\u00A0';
        inner.appendChild(space);
        index += 1;
        return;
      }

      [...token].forEach((char) => {
        const span = document.createElement('span');
        span.className = 'cd-contact-stage__char';
        span.style.setProperty('--cd-contact-char-index', index);
        span.textContent = char;
        inner.appendChild(span);
        index += 1;
      });
    });

    heading.textContent = '';
    heading.appendChild(inner);
  };

  const showSection = (section) => {
    section.classList.add('cd-contact-stage--visible');
  };

  const initSection = (section, observer) => {
    if (!section) return;

    splitHeading(section);
    section.classList.add('cd-contact-stage--ready');

    if ('IntersectionObserver' in window && observer) {
      observer.observe(section);
    } else {
      showSection(section);
    }
  };

  const initAll = (root = document) => {
    const sections = root.querySelectorAll(SELECTOR);
    if (!sections.length) return;

    let observer = null;

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            showSection(entry.target);
            observer.unobserve(entry.target);
          });
        },
        {
          root: null,
          threshold: 0.18,
          rootMargin: '0px 0px -10% 0px',
        },
      );
    }

    sections.forEach((section) => initSection(section, observer));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (!event.target) return;
    if (event.target.matches?.(SELECTOR)) {
      initAll(event.target.parentNode || document);
      return;
    }
    initAll(event.target);
  });
})();

/* Contact section controls the header color/overlap while it is underneath the header. */
(function () {
  const SELECTOR = '.cd-contact-stage';

  const getHeaderElement = () => {
    const selectors = [
      '#shopify-section-header-inline .cd-hero-header__wrapper',
      '#shopify-section-header-inline .t4s-header__wrapper',
      '#shopify-section-header-inline .header-wrapper',
      '#shopify-section-header-inline',
    ];

    for (const selector of selectors) {
      const node = document.querySelector(selector);
      if (!(node instanceof HTMLElement)) continue;

      const rect = node.getBoundingClientRect();
      if (rect.height > 10) return node;
    }

    return document.querySelector('#shopify-section-header-inline');
  };

  const getHeaderMetrics = () => {
    const fallback = window.matchMedia('(max-width: 1024px)').matches ? 68 : 88;
    const header = getHeaderElement();

    if (!(header instanceof HTMLElement)) {
      return { height: fallback, bottom: fallback };
    }

    const rect = header.getBoundingClientRect();
    const height = Math.max(1, Math.round(rect.height || fallback));
    const bottom = Math.max(height, Math.round(rect.bottom || height));

    return { height, bottom };
  };

  const getFirstMainChild = () => {
    const main = document.querySelector('#MainContent') || document.querySelector('main');
    if (!main) return null;

    return Array.from(main.children).find((child) => {
      if (!(child instanceof HTMLElement)) return false;
      return !child.matches('script, style, link, template');
    }) || null;
  };

  const isFirstStage = (section) => {
    const first = getFirstMainChild();
    if (!first) return false;

    const wrapper = section.closest('.shopify-section') || section;
    return first === wrapper || first === section || first.contains(section) || wrapper.contains(first);
  };

  const initContactHeaderTheme = () => {
    const sections = Array.from(document.querySelectorAll(SELECTOR));
    if (!sections.length) return;

    const root = document.documentElement;
    let ticking = false;

    const update = () => {
      ticking = false;

      const { height, bottom } = getHeaderMetrics();
      root.style.setProperty('--cd-contact-detected-header-height', `${height}px`);

      const firstSection = sections.find(isFirstStage);
      root.toggleAttribute('data-cd-contact-first-stage', Boolean(firstSection));

      /* At page load, the Shopify header is still in normal flow, so the
         section begins just below the header. Probing at the actual header
         bottom makes the dark header state active immediately instead of only
         after the first scroll event/sticky-header transition. */
      const probeY = Math.max(bottom - 1, height * 0.5, 1);

      const activeSection = sections.find((section) => {
        const rect = section.getBoundingClientRect();
        const firstAtTop = section === firstSection && window.scrollY <= 6 && rect.top <= bottom + 8;
        return firstAtTop || (rect.top <= probeY && rect.bottom > probeY);
      });

      if (activeSection) {
        const bg = getComputedStyle(activeSection).getPropertyValue('--cd-contact-bg').trim() || '#151515';
        root.setAttribute('data-cd-contact-header', 'light');
        root.style.setProperty('--cd-contact-header-bg-active', bg);
        root.style.setProperty('--cd-pdp-hero-header-bg', bg);
      } else {
        root.removeAttribute('data-cd-contact-header');
        root.style.removeProperty('--cd-contact-header-bg-active');
        root.style.removeProperty('--cd-pdp-hero-header-bg');
      }
    };

    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    update();
    window.requestAnimationFrame(update);
    window.addEventListener('load', update, { once: true });
    [50, 150, 350, 700].forEach((delay) => window.setTimeout(update, delay));

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    window.addEventListener('orientationchange', requestUpdate, { passive: true });
    window.addEventListener('pageshow', requestUpdate, { passive: true });
    document.addEventListener('shopify:section:load', requestUpdate);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initContactHeaderTheme, { once: true });
  } else {
    initContactHeaderTheme();
  }
})();
